import { Close } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  FormControl,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SyntheticEvent, useCallback, useState } from "react";
import { useGetIntakesQuery } from "src/api/services/intakesApi";
import { Intake } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props<T> {
  open: boolean;
  onClose: () => void;
  component: string;
  options?: T[];
  table: string;
  dropdownLabel?: string;
  onSubmit: (i: number) => void;
  loading: boolean;
}

function CopyItemModal<T>({
  onClose,
  open,
  component,
  onSubmit,
  loading,
}: Props<T>) {
  const { selected, setSelected } = useSelection();
  const [selectedIntake, setSelectedIntake] = useState<Intake | null>(null);

  const { data: intakes = [] } = useGetIntakesQuery({
    active: false,
  });

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSelectIntake = useCallback(
    (_: SyntheticEvent<Element | Event>, value: Intake | null) => {
      setSelectedIntake(value);
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (selectedIntake == null) return;

    onSubmit(selectedIntake.id);
    setSelected([]);
    handleClose();
  }, [handleClose, onSubmit, selectedIntake, setSelected]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={desktopSx} gap={3} component={"form"} onSubmit={handleSubmit}>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" component="h5" fontWeight={700}>
            Copy {component}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Alert severity="warning">
          <Typography
            color="warning"
            sx={{ textDecoration: "underline" }}
            variant="h6"
          >
            Items selected
          </Typography>
          <Typography fontWeight={600} variant="h6">
            {selected.join(", ")}
          </Typography>
        </Alert>
        <FormControl>
          <Autocomplete
            options={intakes}
            getOptionLabel={(intake) =>
              `${intake.id}: ${intake.product.name} - ${intake.visit_type}`
            }
            renderInput={(params) => (
              <TextField {...params} required label="Intake" />
            )}
            onChange={handleSelectIntake}
            value={selectedIntake}
          />
        </FormControl>
        <LoadingButton loading={loading} type="submit">
          Copy items
        </LoadingButton>
      </Stack>
    </Modal>
  );
}

export default CopyItemModal;
